import { useState, type FormEvent } from "react";
import { doc, increment, orderBy, updateDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import { useCouple } from "../hooks/useCouple";
import { useCoupleCollection } from "../hooks/useCoupleCollection";
import { Fab } from "../components/Fab";
import { Sheet } from "../components/Sheet";
import type { SavingsGoal } from "../types";

function formatEuro(cents: number) {
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

export function SavingsGoals() {
  const { user } = useAuth();
  const { couple, profile } = useCouple();
  const { items, loading, add, remove } = useCoupleCollection<SavingsGoal>(
    couple?.id ?? null,
    "savingsGoals",
    orderBy("createdAt", "asc")
  );
  const [title, setTitle] = useState("");
  const [target, setTarget] = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);

  const [contributeTarget, setContributeTarget] = useState<SavingsGoal | null>(null);
  const [contributeAmount, setContributeAmount] = useState("");

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const targetAmountCents = Math.round(parseFloat(target.replace(",", ".")) * 100);
    if (!title.trim() || !targetAmountCents || !user || !profile) return;
    await add({
      title: title.trim(),
      targetAmountCents,
      currentAmountCents: 0,
      createdByUid: user.uid,
      createdByName: profile.displayName,
    });
    setTitle("");
    setTarget("");
    setSheetOpen(false);
  }

  function openContribute(goal: SavingsGoal) {
    setContributeAmount("");
    setContributeTarget(goal);
  }

  async function handleContribute(sign: 1 | -1) {
    if (!contributeTarget) return;
    const amountCents = Math.round(parseFloat(contributeAmount.replace(",", ".")) * 100);
    if (!amountCents) return;
    await updateDoc(doc(db, "couples", couple!.id, "savingsGoals", contributeTarget.id), {
      currentAmountCents: increment(sign * amountCents),
    });
    setContributeTarget(null);
  }

  return (
    <main className="mx-auto min-h-dvh max-w-md px-5 pb-28 pt-6">
      <h1 className="font-display text-[1.75rem] font-semibold tracking-tight text-plum-900 dark:text-night-accent">Sparziele</h1>
      <p className="mt-1 text-sm text-ink-400 dark:text-night-faint">Wofür ihr gerade zusammen spart.</p>

      {loading ? (
        <p className="mt-6 text-sm text-ink-400 dark:text-night-faint">Lädt…</p>
      ) : items.length === 0 ? (
        <p className="mt-6 text-sm text-ink-400 dark:text-night-faint">Noch keine Sparziele.</p>
      ) : (
        <ul className="mt-5 space-y-3">
          {items.map((g) => {
            const pct = g.targetAmountCents > 0 ? Math.min(100, (g.currentAmountCents / g.targetAmountCents) * 100) : 0;
            return (
              <li key={g.id} className="card rounded-2xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <p className="font-semibold text-ink-900 dark:text-night-text">{g.title}</p>
                  <button onClick={() => remove(g.id)} aria-label="Löschen" className="flex-shrink-0 text-ink-400 dark:text-night-faint">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4.5 w-4.5">
                      <path d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m-9 0 1 13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1l1-13" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
                <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-cream-200 dark:bg-night-border">
                  <div className="h-full rounded-full bg-gold-500" style={{ width: `${pct}%` }} />
                </div>
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-ink-600 dark:text-night-muted">
                    {formatEuro(g.currentAmountCents)} <span className="text-ink-400 dark:text-night-faint">von {formatEuro(g.targetAmountCents)}</span>
                  </span>
                  <button onClick={() => openContribute(g)} className="font-medium text-plum-700 underline-offset-2 hover:underline dark:text-night-accent">
                    Betrag eintragen
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <Fab onClick={() => setSheetOpen(true)} label="Sparziel anlegen" />

      <Sheet open={sheetOpen} onClose={() => setSheetOpen(false)} title="Neues Sparziel">
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            type="text"
            placeholder="Ziel, z.B. Urlaub in Italien"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="h-12 w-full rounded-xl border border-cream-200 bg-white px-4 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
          />
          <input
            type="text"
            inputMode="decimal"
            placeholder="Zielbetrag in €"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            className="h-12 w-full rounded-xl border border-cream-200 bg-white px-4 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
          />
          <button
            type="submit"
            disabled={!title.trim() || !target.trim()}
            className="h-12 w-full rounded-xl bg-plum-700 text-sm font-semibold text-cream-50 disabled:opacity-50"
          >
            Sparziel anlegen
          </button>
        </form>
      </Sheet>

      <Sheet open={!!contributeTarget} onClose={() => setContributeTarget(null)} title={contributeTarget?.title ?? ""}>
        <div className="space-y-3">
          <input
            type="text"
            inputMode="decimal"
            placeholder="Betrag in €"
            value={contributeAmount}
            onChange={(e) => setContributeAmount(e.target.value)}
            className="h-12 w-full rounded-xl border border-cream-200 bg-white px-4 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
          />
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => handleContribute(-1)}
              disabled={!contributeAmount.trim()}
              className="h-12 flex-1 rounded-xl border border-garnet-600 text-sm font-semibold text-garnet-600 disabled:opacity-50"
            >
              − Abziehen
            </button>
            <button
              type="button"
              onClick={() => handleContribute(1)}
              disabled={!contributeAmount.trim()}
              className="h-12 flex-1 rounded-xl bg-plum-700 text-sm font-semibold text-cream-50 disabled:opacity-50"
            >
              + Hinzufügen
            </button>
          </div>
        </div>
      </Sheet>
    </main>
  );
}
